// A modal that lets the office pick an existing Xero invoice (from the InvoiceCache) to attach to
// a job or call-out, for the historical backlog (Session AO Item 3). It only SELECTS an invoice;
// the caller's onSelect does the linking. Never creates or modifies a Xero invoice. The list is
// searchable by customer, site or invoice number.
import { useEffect, useMemo, useState } from "react";
import { ModalShell } from "./ModalShell";
import { api } from "../lib/api";
import type { InvoiceListItem } from "../lib/api";
import { gbpExact } from "../lib/format";
import { EmptyState } from "./EmptyState";
import { Loader } from "./Loader";

export function LinkInvoiceModal({
  open,
  onSelect,
  onClose,
  busy,
  error,
}: {
  open: boolean;
  onSelect: (invoiceId: string) => void;
  onClose: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    let live = true;
    setState("loading");
    setQ("");
    api.invoices
      .list()
      .then((rows) => {
        if (!live) return;
        setInvoices(rows);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return invoices;
    return invoices.filter((inv) =>
      [inv.customerName, inv.siteName ?? "", inv.xeroInvoiceId].some((s) => s.toLowerCase().includes(needle)),
    );
  }, [invoices, q]);

  if (!open) return null;

  return (
    <ModalShell ariaLabel="Link to an invoice" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="flex max-h-[80vh] flex-col p-5">
        <h2 className="mb-1 font-heading text-xl text-ele-strong">Link to an invoice</h2>
        <p className="mb-3 text-sm text-ele-body">
          Choose the Xero invoice that covers this work. This only records the link, it does not change the invoice.
        </p>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by customer or site"
          className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
        />
        {error && <p role="alert" className="mb-2 text-sm text-ele-danger">{error}</p>}
        <div className="min-h-0 flex-1 overflow-y-auto rounded-ele border border-ele-purple-accent">
          {state === "loading" ? (
            <Loader label="Loading invoices..." rows={3} className="p-4" />
          ) : state === "error" ? (
            <p role="alert" className="p-4 text-sm text-ele-strong">Could not load invoices. Please try again in a moment.</p>
          ) : filtered.length === 0 ? (
            <EmptyState
              compact
              role={invoices.length === 0 ? undefined : "status"}
              className="px-4"
              message={invoices.length === 0 ? "No invoices found in Xero yet." : "No invoices match your search."}
            />
          ) : (
            <ul className="divide-y divide-ele-purple-accent">
              {filtered.map((inv) => (
                <li key={inv.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSelect(inv.id)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-ele-inset disabled:opacity-50"
                  >
                    <span>
                      <span className="text-ele-strong">{inv.customerName}</span>
                      {inv.siteName ? <span className="text-ele-body opacity-70"> | {inv.siteName}</span> : null}
                      <span className="block font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{inv.status}</span>
                    </span>
                    <span className="shrink-0 text-ele-strong">{gbpExact(inv.amount)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
    </ModalShell>
  );
}
